import { MissionAssessmentRequestSelectDto, MissionAssessmentRequestStatus } from '@@types/api.types';
import { AvatarList } from '@components/shared/avatar-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Button } from '@components/ui/button';
import { countries } from '@utils/constants';
import { formatDistanceToNow } from 'date-fns';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExpandableText } from '../../../shared/expandable-text.component';

export interface MissionAssessmentRequestCardProps {
  missionAssessmentRequest: MissionAssessmentRequestSelectDto;
}

export const MissionAssessmentRequestCard: React.FC<MissionAssessmentRequestCardProps> = (props) => {
  const { missionAssessmentRequest } = props;
  const { technology, status, veteranServices } = missionAssessmentRequest;
  const missionAssessment = missionAssessmentRequest.missionAssessments?.[0];

  const veteranCountryNames = useMemo(() => {
    if (
      !missionAssessmentRequest.veteranCountriesOfService ||
      missionAssessmentRequest.veteranCountriesOfService.length === 0
    ) {
      return [];
    }

    return missionAssessmentRequest.veteranCountriesOfService
      .map((code) => {
        const country = countries.find((c) => c.code === code);
        return country?.name ?? code;
      })
      .filter(Boolean);
  }, [missionAssessmentRequest.veteranCountriesOfService]);

  const veteranCountryDisplay = useMemo(() => {
    if (veteranCountryNames.length === 0) {
      return undefined;
    }
    if (veteranCountryNames.length === 1) {
      return veteranCountryNames[0];
    }
    return veteranCountryNames.join(', ');
  }, [veteranCountryNames]);

  const serviceAvatarItems = useMemo(() => {
    if (!veteranServices || veteranServices.length === 0) {
      return [];
    }

    return veteranServices.map((service) => {
      let tooltip = service.name;
      if (service.parentService?.name) {
        const parentCountryName = service.parentService.name;
        const country = countries.find((c) => c.name === parentCountryName);
        const countryCode = country?.code ?? parentCountryName;
        tooltip = `${countryCode} - ${service.name}`;
      }

      return {
        altText: service.name,
        imageUrl: service.slug ? `/images/services/${service.slug}.png` : undefined,
        initialText: service.name,
        tooltip,
      };
    });
  }, [veteranServices]);

  let buttonText = 'Start Assessment';
  let buttonLink = `/technology/${technology.id}/assessment/create?requestId=${missionAssessmentRequest.id}`;
  if (status === MissionAssessmentRequestStatus.IN_PROGRESS) {
    buttonText = 'Continue Assessment';
    buttonLink = `/technology/${technology.id}/assessment/${missionAssessment?.id}/edit?requestId=${missionAssessmentRequest.id}`;
  } else if (status === MissionAssessmentRequestStatus.ASSESSED) {
    buttonText = 'View Assessment';
    buttonLink = `/technology/${technology.id}/assessment/${missionAssessment?.id}/edit?requestId=${missionAssessmentRequest.id}`;
  }

  return (
    <div className="w-full p-4 bg-white border border-gray-200 rounded-lg flex flex-col shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full mb-4 sm:mb-0 flex justify-between">
          <Link
            className="text-lg font-medium text-gray-800 hover:underline"
            to={`/technology/${technology.id}`}
          >
            {technology.name}
          </Link>

          <div className="flex items-stretch gap-4 text-sm">
            <div className="text-sm text-gray-500 text-right">
              <BlueLabel text="Requested" />
              <p className="text-gray-800">
                {formatDistanceToNow(new Date(missionAssessmentRequest.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200 my-4" />

      <div className="flex flex-wrap items-start gap-4">
        {missionAssessmentRequest.use && (
          <TextWithLabel
            label="Use"
            title={missionAssessmentRequest.use.name}
            className="text-sm max-w-[200px]"
          />
        )}
        {missionAssessmentRequest.domain && (
          <TextWithLabel
            label="Domain"
            title={missionAssessmentRequest.domain.name}
            className="text-sm max-w-[200px]"
          />
        )}

        <div className="ml-auto hidden sm:block">
          <Link to={buttonLink}>
            <Button
              variant="primary"
              className="w-full sm:w-auto"
            >
              {buttonText}
            </Button>
          </Link>
        </div>
      </div>

      {veteranCountryDisplay && (
        <div className="flex flex-wrap items-stretch gap-4 mt-4">
          <TextWithLabel
            label={veteranCountryNames.length > 1 ? 'Target Nations' : 'Target Nation'}
            title={veteranCountryDisplay}
            className="text-sm whitespace-nowrap mt-auto"
          />
          {serviceAvatarItems.length > 0 && (
            <AvatarList
              items={serviceAvatarItems}
              label={serviceAvatarItems.length > 1 ? 'Services' : 'Service'}
              limit={3}
              size="sm"
            />
          )}
        </div>
      )}

      {missionAssessmentRequest.message && (
        <div className="mt-4">
          <ExpandableText
            text={missionAssessmentRequest.message}
            className="text-xs text-gray-600 max-w-full"
          />
        </div>
      )}

      <div className="mt-4 ml-auto sm:hidden">
        <Link to={buttonLink}>
          <Button
            variant="primary"
            className="w-full sm:w-auto"
          >
            {buttonText}
          </Button>
        </Link>
      </div>
    </div>
  );
};
